import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TeamMemberRow {
  id: string;
  company_id: string;
  name: string;
  email: string;
  // Nullable since the 20260430020000 migration: admin pre-seeded rows
  // typically lack birthday and start_date until the user fills them in.
  // Existing UI that calls new Date(birthday) needs a null guard.
  birthday: string | null;
  start_date: string | null;
  dietary_requirements: string[];
  // Allergies are kept separate from dietary_requirements (added in the
  // 20260430030000 migration). Allergies are medical and treated with
  // stronger UX cues than preferences. Older rows default to [].
  allergies: string[];
  // MS390 Bug #8: distinguish "unknown" from "explicitly none".
  // Without these flags, an empty allergies[] array is ambiguous
  // and the "needs attention" banner fires for every team member
  // forever. Buyer ticks "No allergies" / "No dietary requirements"
  // on the form to set these to true. See migration
  // 20260520030000_ms390_team_members_allergies_confirmed.sql.
  allergies_confirmed: boolean;
  dietary_confirmed: boolean;
  department: string | null;
  office_location: string | null;
  is_active: boolean;
  created_at: string;
  // Provenance for the missing-data widget. 'admin_seeded' rows surface
  // a "please confirm" treatment; user-entered ones just need their gaps
  // closed. Older rows predate this column and resolve to null.
  data_source: "admin_seeded" | "user_csv" | "user_manual" | null;
  enriched_at: string | null;
  // Sprint 1.2/1.3, autopilot eligibility, derived by the LLM scorer
  // from the rich-profile fields on team_members. Drives the autopilot
  // badge on the Team page tile + the per-employee autopilot status on
  // the profile page.
  auto_eligible: boolean | null;
  auto_eligibility_score: number | null;
  /** P1 personalization capture — last paid gift summary. */
  last_gift_summary: string | null;
  /** P1 personalization capture — inferred preference tags. */
  inferred_tags: string[];
}

/**
 * Count of active team members for a company. Returns 0 when companyId is
 * null so callers can render a dashboard stat without a guard of their own.
 */
export function useActiveTeamMemberCount(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["team_members", companyId, "active-count"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<number> => {
      if (!companyId) return 0;
      const { count, error } = await supabase
        .from("team_members")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .eq("is_active", true);
      if (error) throw error;
      return count ?? 0;
    },
  });
}

/**
 * Full team-member list for a company, ordered by name. Used by the /team
 * page. Search, filter, and sort are all pure client-side derivations on
 * top of this one cached list, so switching tabs or sort orders is a cache
 * hit. Mutations against team_members should invalidate
 * ["team_members", companyId] (the shared prefix) to refresh both this
 * query and the active-count query at once.
 */
export function useTeamMembers(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["team_members", companyId, "all"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<TeamMemberRow[]> => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .eq("company_id", companyId)
        .order("name")
        // MS397: safety ceiling for enterprise customers; 500 covers all
        // realistic team sizes. A "showing first 500" notice can be added
        // to /team if the count ever exceeds this threshold.
        .limit(500);
      if (error) throw error;
      return (data as TeamMemberRow[] | null) ?? [];
    },
  });
}
